import { useEffect, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { actualizarPrevistoPlanPago } from '../../services/planPagoService';
import EditorPeriodos24476, { aFechaPeriodo, aMesPeriodo, mesAnio, mesesDePeriodo, periodoValido } from './EditorPeriodos24476';

const desdeTramite = (t) => ({
    previstoMonotributo: t.previstoMonotributo ?? null,
    previstoMeses27705: t.previstoMeses27705 ?? '',
    previstoObservacion: t.previstoObservacion || '',
    periodos: (t.periodos24476Previstos || []).map((p) => ({ desde: aMesPeriodo(p.desde), hasta: aMesPeriodo(p.hasta) })),
});

const fecha = (iso) => {
    if (!iso) return '';
    const [a, m, d] = String(iso).slice(0, 10).split('-');
    return `${d}/${m}/${a}`;
};

/**
 * "Apertura del plan" en el detalle del plan de pago (15-oct-2026). Se carga con anticipación lo
 * PREVISTO para cuando el cliente vuelva de ANSES (inscribirlo al monotributo, cuántos meses de la
 * Ley 27.705, qué períodos de la 24.476). En la atención de la apertura se ve esto y se registra lo
 * que se hizo. Accesos directos a modificar los aportes (calculadora) y los hijos.
 */
export default function TarjetaAperturaPlan({ tramite, onCambio }) {
    const [form, setForm] = useState(() => desdeTramite(tramite || {}));
    const [guardando, setGuardando] = useState(false);
    const idTramite = tramite?.idTramite;

    // Se reinicia al cambiar de trámite o cuando el backend devuelve otro previsto (comparado por
    // valor: el objeto del trámite cambia en cada recarga y no debe pisar lo que se está editando).
    const firmaPrevisto = JSON.stringify(desdeTramite(tramite || {}));
    useEffect(() => {
        setForm(JSON.parse(firmaPrevisto));
    }, [idTramite, firmaPrevisto]);

    const cuil = tramite?.cliente?.cuil || tramite?.idCliente;
    const abierto = Boolean(tramite?.aperturaEn);
    const comprados = tramite?.periodos24476Cliente || [];
    const periodosOk = form.periodos.every(periodoValido);

    const guardar = async () => {
        if (!periodosOk) {
            toast.error('Revisá los períodos de la Ley 24.476: cada uno necesita desde y hasta.');
            return;
        }
        setGuardando(true);
        try {
            await actualizarPrevistoPlanPago(idTramite, {
                previstoMonotributo: form.previstoMonotributo,
                previstoMeses27705: form.previstoMeses27705 === '' ? null : Number(form.previstoMeses27705),
                previstoObservacion: form.previstoObservacion.trim() || null,
                periodos24476Previstos: form.periodos.map((p) => ({ desde: aFechaPeriodo(p.desde), hasta: aFechaPeriodo(p.hasta) })),
            });
            toast.success('Previsto guardado');
            onCambio?.();
        } catch (e) {
            toast.error(e?.response?.data?.message || 'No se pudo guardar lo previsto');
        } finally {
            setGuardando(false);
        }
    };

    const opcionMonotributo = (valor, texto) => (
        <Button
            size="sm"
            variant={form.previstoMonotributo === valor ? 'primary' : 'outline-secondary'}
            aria-pressed={form.previstoMonotributo === valor}
            onClick={() => setForm((f) => ({ ...f, previstoMonotributo: valor }))}
        >
            {texto}
        </Button>
    );

    return (
        <div className="row g-4 mt-1">
            <div className="col-12">
                <div className="card shadow-lg border-0 rounded-4 p-4">
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                        <h4 className="fw-bold text-primary mb-0">
                            <i className="bi bi-clipboard-check me-2"></i>Apertura del plan
                        </h4>
                        <span className={`badge rounded-pill ${abierto ? 'bg-success' : 'bg-warning text-dark'}`}>
                            {abierto
                                ? `Abierto el ${fecha(tramite.aperturaEn)}${tramite.aperturaPor ? ` · ${tramite.aperturaPor.split('@')[0]}` : ''}`
                                : 'Pendiente: se abre cuando el cliente vuelve de ANSES'}
                        </span>
                        {cuil && (
                            <div className="ms-auto d-flex flex-wrap gap-2">
                                <a className="btn btn-sm btn-outline-primary" href={`/herramientas/historial-laboral?cuil=${cuil}`} target="_blank" rel="noopener noreferrer">
                                    <i className="bi bi-calculator me-1"></i>Modificar aportes
                                </a>
                                <a className="btn btn-sm btn-outline-primary" href={`/clientes/vista-detallada/${cuil}`} target="_blank" rel="noopener noreferrer">
                                    <i className="bi bi-people me-1"></i>Modificar hijos
                                </a>
                            </div>
                        )}
                    </div>

                    {abierto && tramite.aperturaObservacion && (
                        <div className="alert alert-success border-0 rounded-3 py-2">{tramite.aperturaObservacion}</div>
                    )}

                    <h6 className="fw-semibold text-secondary">Lo previsto</h6>
                    <div className="row g-3">
                        <div className="col-md-4">
                            <div className="form-label small fw-semibold mb-1">Monotributo</div>
                            <div className="d-flex gap-2" role="group" aria-label="Inscribir al monotributo">
                                {opcionMonotributo(true, 'Inscribirlo')}
                                {opcionMonotributo(false, 'No')}
                                {opcionMonotributo(null, 'Sin definir')}
                            </div>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label small fw-semibold mb-1" htmlFor="prev-meses-27705">Meses Ley 27.705</label>
                            <Form.Control id="prev-meses-27705" type="number" min="0" size="sm" value={form.previstoMeses27705}
                                onChange={(e) => setForm((f) => ({ ...f, previstoMeses27705: e.target.value }))} />
                            {tramite?.resumenAportes?.mesesFaltantes != null && (
                                <div className="small text-muted">Según la calculadora faltan {tramite.resumenAportes.mesesFaltantes} meses.</div>
                            )}
                        </div>
                        <div className="col-md-5">
                            <label className="form-label small fw-semibold mb-1" htmlFor="prev-obs">Observación</label>
                            <Form.Control id="prev-obs" as="textarea" rows={1} size="sm" value={form.previstoObservacion}
                                onChange={(e) => setForm((f) => ({ ...f, previstoObservacion: e.target.value }))} />
                        </div>
                        <div className="col-12">
                            <div className="form-label small fw-semibold mb-1">Ley 24.476: períodos a comprar</div>
                            <EditorPeriodos24476 periodos={form.periodos} idBase="prev-24476"
                                onChange={(periodos) => setForm((f) => ({ ...f, periodos }))} />
                        </div>
                    </div>
                    <div className="d-flex justify-content-end mt-2">
                        <Button size="sm" onClick={guardar} disabled={guardando || !periodosOk}>
                            {guardando ? <Spinner animation="border" size="sm" className="me-1" /> : <i className="bi bi-save me-1" />}
                            Guardar lo previsto
                        </Button>
                    </div>

                    <hr />
                    <h6 className="fw-semibold text-secondary">Ley 24.476 ya comprada por el cliente</h6>
                    {comprados.length === 0 ? (
                        <div className="small text-muted">Ninguno registrado. Se cargan al abrir el plan o desde la ficha del cliente.</div>
                    ) : (
                        <div className="d-flex flex-wrap gap-2">
                            {comprados.map((p) => (
                                <span key={p.id} className="badge rounded-pill bg-light text-dark border">
                                    {mesAnio(p.desde)} – {mesAnio(p.hasta)} · {mesesDePeriodo({ desde: aMesPeriodo(p.desde), hasta: aMesPeriodo(p.hasta) })} meses
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
